import {useMutation} from '@github-ui/react-query'
import {createRepositoryMilestoneMutation} from '@github-ui/server-persisted-queries'
import type {CreateRepositoryMilestoneMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import {formatFormErrorMessage} from './utils/format-form-error-message'

export type CreateMilestoneFormInput = {
  title: string
  description?: string | null
  dueOn?: string | null
}

export type CreatedMilestone = NonNullable<
  NonNullable<CreateRepositoryMilestoneMutationMutation['createMilestone']>['milestone']
>

type UseCreateRepositoryMilestoneOptions = {
  repositoryId: string | null | undefined
  repositoryNameWithOwner: string | null | undefined
  setSubmissionErrors: (value: string | null) => void
  onSuccess: (milestone: CreatedMilestone) => void | Promise<void>
}

// Creates a milestone through the `createRepositoryMilestoneMutation` server-persisted
// query and navigates to it on success (via `onSuccess`). There is no optimistic UI:
// the create page unmounts on success, so there is no list to overlay.
export function useCreateRepositoryMilestone({
  repositoryId,
  repositoryNameWithOwner,
  setSubmissionErrors,
  onSuccess,
}: UseCreateRepositoryMilestoneOptions) {
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<CreateRepositoryMilestoneMutationMutation, Error, CreateMilestoneFormInput>({
    mutationFn: async (input: CreateMilestoneFormInput) => {
      // Normalize the nullable repository fields here rather than defaulting them at
      // the call site: without a repository we can't build a valid request, so surface
      // the generic create error instead of POSTing to a malformed URL.
      if (!repositoryId || !repositoryNameWithOwner) {
        throw new Error(LABELS.milestoneCreateError)
      }

      let response: Response
      try {
        response = await reactFetchJSON(`/${repositoryNameWithOwner}/milestones/new`, {
          method: 'POST',
          body: {
            queryId: createRepositoryMilestoneMutation,
            repositoryId,
            title: input.title,
            description: input.description,
            dueOn: input.dueOn,
          },
        })
      } catch {
        throw new Error(LABELS.milestoneCreateError)
      }

      if (!response.ok) {
        throw new Error(LABELS.milestoneCreateError)
      }

      const result: {data: CreateRepositoryMilestoneMutationMutation} = await response.json()

      // Payload validation errors (e.g. "Title has already been taken") are threaded
      // through the thrown error's `cause` so onError can format them for the user.
      // A missing milestone without errors surfaces the generic create error.
      const errors = result.data.createMilestone?.errors
      if (errors && errors.length > 0) {
        throw new Error(LABELS.milestoneCreateError, {cause: errors})
      }
      if (!result.data.createMilestone?.milestone) {
        throw new Error(LABELS.milestoneCreateError)
      }

      return result.data
    },
    onError: (error: Error) => {
      // Payload validation errors are threaded via `error.cause`; format and surface
      // them. Every other failure — transport error, non-2xx, a missing milestone, or
      // a malformed body — surfaces the generic create error.
      if (error.cause && Array.isArray(error.cause)) {
        setSubmissionErrors(formatFormErrorMessage(error.cause))
      } else {
        setSubmissionErrors(LABELS.milestoneCreateError)
      }
      setIsSubmittingRef.current(false)
    },
    onSuccess: async data => {
      // Reset the submitting state before handing off to the caller. The current
      // consumer navigates away on success (unmounting the form), but resetting here
      // keeps the hook correct for callers that stay mounted.
      setIsSubmittingRef.current(false)
      const created = data.createMilestone?.milestone
      if (created) {
        await onSuccess(created)
      }
    },
  })

  return useCallback(
    (input: CreateMilestoneFormInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
